<template>
    <div>
        <div
            v-if="title.length !== 0"
            class="text-lg xs:text-xl text-gray-900 pb-2.5 m-0 font-bold"
        >
            {{ title }}
        </div>
        <slot name="subtitle">
            <h4
                v-if="subtitle.length !== 0"
                class="text-gray-900 mb-2.5 font-normal"
            >
                {{ subtitle }}
            </h4>
        </slot>
        <div class="relative">
            <div
                ref="license"
                class="terms-frame mb-2.5 overflow-y-auto"
                :class="[height]"
            >
                <transition
                    name="opacity"
                    mode="out-in"
                >
                    <div
                        v-if="!content.length"
                        id="skeleton"
                        class="p-3.5 md:p-5 pb-8"
                    >
                        <SkeletonTerm
                            v-for="n in 1"
                            :key="n"
                        />
                    </div>
                    <div
                        v-else
                        class="terms-content p-2.5 xs:p-3.5 md:p-5 md:pb-8 text-sm xs:text-base"
                    >
                        <p
                            v-if="innerTitle"
                            class="text-lg font-bold text-gray-900 mb-2"
                        >
                            {{ innerTitle }}
                        </p>
                        <div v-html="content" />
                    </div>
                </transition>
            </div>
            <div
                v-if="needLayer"
                v-show="!isScrollToBottom"
                class="terms-layer"
            />
        </div>
        <Checkbox
            v-if="needCheckbox"
            v-model:checkedValues="isAgree"
            :value="true"
            :disabled="!isScrollToBottom"
            :text="checkboxText"
            :center="checkboxCenter"
            class="items-baseline mb-0 mr-0"
            @click="isShowMessage = !isScrollToBottom"
        >
            <slot name="checkbox" />
        </Checkbox>
        <p
            v-if="needCheckbox"
            class="text-error text-sm min-h-[1.5rem]"
            :class="[checkboxCenter? 'text-center' : 'ml-6']"
        >
            <span v-show="isShowMessage && !isScrollToBottom">請閱讀完畢同意條款</span>
        </p>
        <div
            v-if="needGuideArrow"
            class="-mt-6 h-7 md:h-9"
        >
            <SvgIcon
                v-show="!isScrollToBottom"
                icon-name="expand_more"
                class="mx-auto text-green-500 h-7 w-7 md:h-9 md:w-9 animate-fade"
            />
        </div>
        <slot name="action">
            <div class="text-center">
                <button
                    v-if="needBtn"
                    class="btn gradient-primary rounded-full py-3 px-5"
                    :disabled="disconnectScroll ? false : !isScrollToBottom"
                    @click="$emit('clickBtn');"
                >
                    <!-- :disabled="disconnectScroll ? false : needCheckbox? !isAgree : !isScrollToBottom" -->
                    {{ btnText }}
                </button>
            </div>
        </slot>
        <Loading :is-loading="isLoading" />
    </div>
</template>

<script>
import { ref, onMounted, watchEffect } from 'vue';
import SkeletonTerm from '@/components/skeletons/SkeletonTerm.vue';
import { useEventListener, useMutationObserver } from '@vueuse/core';
import Loading from '@/components/Loading.vue';
import Checkbox from '@/components/checkboxs/Checkbox.vue';
export default {
    components: {
        SkeletonTerm,
        Loading,
        Checkbox
    },
    props: {
        /*
        --標題類--
        title {String} 在條款視窗上方加入標題 default: ''
        subtitle {String} 在條款視窗上方加入副標題 default: ''
        innerTitle {String} 在條款視窗內加入副標題 default: ''
        --條款類--
        height {String} 設定條款視窗高度 default: 'h-[55vh]'
        content {String} 條款視窗內文 default: ''
        needLayer {Boolean} 條款視窗加入底部模糊 default: false
        needGuideArrow {Boolean} 是否需要在條款視窗底部加入箭頭 default: false
        disconnectScroll {Boolean} 斷開條款視窗滾動到底部的偵測 default: false
        --按鈕類--
        needBtn {Boolean} 加入預設按鈕 default:false
        btnText {String} 預設按鈕文字 default: ''
        isLoading {Boolean} 按下預設按鈕後開始loading default: false
        --checkbox類--
        needCheckbox {Boolean} 加入條款視窗下的checkbox default: false
        checkboxText {String} 預設checkbox文字 default:''
        */
        isLoading: {
            type: Boolean,
            default: false,
        },
        height: {
            type: String,
            default: 'h-[55vh]'
        },
        content: {
            type: String,
            default: ''
        },
        btnText: {
            type: String,
            default: ''
        },
        checkboxText: {
            type: String,
            default: ''
        },
        needLayer: {
            type: Boolean,
            default: false,
        },
        needCheckbox: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: ''
        },
        subtitle: {
            type: String,
            default: ''
        },
        innerTitle: {
            type: String,
            default: ''
        },
        disconnectScroll: {
            type: Boolean,
            default: false,
        },
        needBtn: {
            type: Boolean,
            default: false,
        },
        needGuideArrow: {
            type: Boolean,
            default: false,
        },
        checkboxCenter: {
            type: Boolean,
            default: false,
        }
    },
    /** isScrollToBottom 滾到底部觸發
     *  clickBtn 按按鈕觸發
     *  isAgree 傳出是否確認
    */
    emits: ['isScrollToBottom', 'clickBtn', 'isAgree'],
    setup(props, { emit }) {
        const license = ref(null);
        const isUserScrollToBottom = ref(false);
        const isScrollToBottom = ref(false);
        const isAgree = ref(false);
        const isShowMessage = ref(false);
        onMounted(() => {
            let scrollHeight = null;
            let scrollTop = null;
            let clientHeight = null;
            if (!props.disconnectScroll) {
                scrollHeight = license.value.scrollHeight;
                scrollTop = license.value.scrollTop;
                clientHeight = license.value.clientHeight;
                isScrollToBottom.value = scrollHeight < clientHeight + 10;
                /** 觀察DOM元素節點的變化，childList所有子節點 */
                useMutationObserver(license.value, (mutations) => {
                    let skeletonIsRemoved = false;
                    mutations.forEach(i => {
                        if (i.removedNodes[0]?.id === 'skeleton') skeletonIsRemoved = true;
                    });
                    if (skeletonIsRemoved) {
                        /** 抓DOM資料 */
                        scrollHeight = license.value.scrollHeight;
                        // scrollTop = license.value.scrollTop;
                        clientHeight = license.value.clientHeight;
                        isScrollToBottom.value = scrollHeight < clientHeight + 10;
                    }
                }, { childList: true });
                useEventListener(license.value, 'scroll', () => {
                    /** 抓DOM資料 */
                    scrollHeight = license.value.scrollHeight;
                    scrollTop = license.value.scrollTop;
                    clientHeight = license.value.clientHeight;
                    if (scrollHeight - scrollTop < clientHeight + 10) {
                        isUserScrollToBottom.value = true;
                        isScrollToBottom.value = isUserScrollToBottom.value;
                    }
                });
                useEventListener(window, 'resize', () => {
                    /** 抓DOM資料 */
                    scrollHeight = license.value.scrollHeight;
                    scrollTop = license.value.scrollTop;
                    clientHeight = license.value.clientHeight;
                    if (!isUserScrollToBottom.value) {
                        // isScrollToBottom.value = scrollHeight - scrollTop < clientHeight + 10;
                        isScrollToBottom.value = scrollHeight < clientHeight + 10;
                    }
                });
            }
        });

        watchEffect(() => {
            emit('isScrollToBottom', isScrollToBottom.value);
            emit('isAgree', isAgree.value);
        });

        return {
            license,
            isScrollToBottom,
            isAgree,
            isShowMessage
        };
    }
};
</script>

<style lang="scss" scoped>
.terms-frame {
    @apply mb-5 rounded bg-gray-50 text-gray-900;
}
:deep(.terms-content) {
    > ul {
        > li {
            @apply mb-2;
        }
    }
}
.terms-layer {
    @apply absolute w-full h-12 bottom-0 bg-gradient-to-b from-transparent to-white;
}
.animate-fade {
    animation: fade 1.3s infinite normal;
}
@keyframes fade {
    0% {
        transform: translateY(-20%);
        opacity: 1;
        animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
    }
    70% {
        transform: translateY(5%);
        opacity: 0.1;
        animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
    }
    100% {
        transform: translateY(10%);
        opacity: 0;
        animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
    }
}
</style>
